import React, { useCallback, useMemo } from 'react'
import { Checkbox, Popconfirm, Spin } from 'antd'
import { includes, isEmpty } from 'lodash'

import { StyledButtonWithMargin } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'

import { useUserHasPermissions } from '../../../account/hooks'
import { useDishesProgressCountsLazyQuery } from '../../../dishes/hooks'
import { MENU_CURATION_TASK_STATUS } from '../../constants'
import {
  useReopenMenuCurationTaskMutation,
  useUpdateMenuCurationTaskStatusFlagMutation,
  useUserHasMenuCurationTaskChangePermission,
} from '../../hooks'
import { menuCurationTaskPropType } from '../../prop-types'

const MenuCurationTaskMenuButtons = ({ menuCurationTask }) => {
  const { userHasMenuChangePermission } =
    useUserHasMenuCurationTaskChangePermission(menuCurationTask)

  const [reopen, { isReopenLoading }] = useReopenMenuCurationTaskMutation()

  const [, { data: dishesProgressCounts }] = useDishesProgressCountsLazyQuery()

  const [updateMenuCurationTaskStatusFlag, { isLoading: isFlagUpdateLoading }] =
    useUpdateMenuCurationTaskStatusFlagMutation()

  const hasPermissionToChangeStatusFlags = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.UPDATE_STATUS_FLAGS,
  )
  const hasPermissionToReopen = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.REOPEN,
  )

  const handleReopen = useCallback(
    () => reopen(menuCurationTask.id),
    [menuCurationTask.id, reopen],
  )

  const handleUnknownWordsCurationDone = useCallback(() => {
    updateMenuCurationTaskStatusFlag(menuCurationTask.id, {
      isUnknownWordsCurationDone: !menuCurationTask.isUnknownWordsCurationDone,
    })
  }, [
    menuCurationTask.id,
    menuCurationTask.isUnknownWordsCurationDone,
    updateMenuCurationTaskStatusFlag,
  ])

  const handleUnknownWordsQaDone = useCallback(() => {
    updateMenuCurationTaskStatusFlag(menuCurationTask.id, {
      isUnknownWordsQaDone: !menuCurationTask.isUnknownWordsQaDone,
    })
  }, [
    menuCurationTask.id,
    menuCurationTask.isUnknownWordsQaDone,
    updateMenuCurationTaskStatusFlag,
  ])

  const handleTierOneDone = useCallback(() => {
    updateMenuCurationTaskStatusFlag(menuCurationTask.id, {
      isTierOneDone: !menuCurationTask.isTierOneDone,
    })
  }, [
    menuCurationTask.id,
    menuCurationTask.isTierOneDone,
    updateMenuCurationTaskStatusFlag,
  ])

  const handleCurationDone = useCallback(() => {
    updateMenuCurationTaskStatusFlag(menuCurationTask.id, {
      isCurationDone: !menuCurationTask.isCurationDone,
    })
  }, [
    menuCurationTask.id,
    menuCurationTask.isCurationDone,
    updateMenuCurationTaskStatusFlag,
  ])

  const handleQaDone = useCallback(() => {
    updateMenuCurationTaskStatusFlag(menuCurationTask.id, {
      isQaDone: !menuCurationTask.isQaDone,
    })
  }, [
    menuCurationTask.id,
    menuCurationTask.isQaDone,
    updateMenuCurationTaskStatusFlag,
  ])

  const handleQcDone = useCallback(() => {
    updateMenuCurationTaskStatusFlag(menuCurationTask.id, {
      isQcDone: !menuCurationTask.isQcDone,
    })
  }, [
    menuCurationTask.id,
    menuCurationTask.isQcDone,
    updateMenuCurationTaskStatusFlag,
  ])

  const pendingWorkMessage = useMemo(() => {
    if (isEmpty(dishesProgressCounts)) {
      return null
    }
    const { doneCount, availableForWorkCount } = dishesProgressCounts
    const remainingCount = availableForWorkCount - doneCount

    if (!remainingCount) {
      return null
    }

    return `There are ${remainingCount} dishes not checked. Are you sure you want to complete your work?`
  }, [dishesProgressCounts])

  const areStatusFlagCheckboxesDisabled = useMemo(
    () => !hasPermissionToChangeStatusFlags || !userHasMenuChangePermission,
    [hasPermissionToChangeStatusFlags, userHasMenuChangePermission],
  )

  const isTierOneDoneCheckboxDisabled = useMemo(
    () =>
      areStatusFlagCheckboxesDisabled ||
      menuCurationTask.taskStatus !==
        MENU_CURATION_TASK_STATUS.TIER_ONE_IN_PROGRESS.value,
    [areStatusFlagCheckboxesDisabled, menuCurationTask.taskStatus],
  )

  const isUnknownWordsCurationDoneCheckboxDisabled = useMemo(
    () =>
      areStatusFlagCheckboxesDisabled ||
      menuCurationTask.taskStatus !==
        MENU_CURATION_TASK_STATUS.UNKNOWN_WORDS_CURATION_IN_PROGRESS.value,
    [areStatusFlagCheckboxesDisabled, menuCurationTask.taskStatus],
  )

  const isUnknownWordsQaDoneCheckboxDisabled = useMemo(
    () =>
      areStatusFlagCheckboxesDisabled ||
      menuCurationTask.taskStatus !==
        MENU_CURATION_TASK_STATUS.UNKNOWN_WORDS_QA_IN_PROGRESS.value,
    [areStatusFlagCheckboxesDisabled, menuCurationTask.taskStatus],
  )

  const isCurationDoneCheckboxDisabled = useMemo(
    () =>
      areStatusFlagCheckboxesDisabled ||
      menuCurationTask.taskStatus !==
        MENU_CURATION_TASK_STATUS.CURATION_IN_PROGRESS.value,
    [areStatusFlagCheckboxesDisabled, menuCurationTask.taskStatus],
  )

  const isQaDoneCheckboxDisabled = useMemo(
    () =>
      areStatusFlagCheckboxesDisabled ||
      menuCurationTask.taskStatus !==
        MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
    [areStatusFlagCheckboxesDisabled, menuCurationTask.taskStatus],
  )

  const isQcDoneCheckboxDisabled = useMemo(
    () =>
      areStatusFlagCheckboxesDisabled ||
      menuCurationTask.taskStatus !==
        MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
    [areStatusFlagCheckboxesDisabled, menuCurationTask.taskStatus],
  )

  if (isFlagUpdateLoading) {
    return <Spin />
  }

  return (
    <>
      {includes(
        [
          MENU_CURATION_TASK_STATUS.RESOLVED.value,
          MENU_CURATION_TASK_STATUS.AUTOMATICALLY_RESOLVED.value,
        ],
        menuCurationTask?.taskStatus,
      ) && (
        <Popconfirm
          title="This will Reopen the Task. Are you sure?"
          onConfirm={handleReopen}
          disabled={!hasPermissionToReopen}
          placement="bottomRight"
        >
          <StyledButtonWithMargin
            loading={isReopenLoading}
            disabled={!hasPermissionToReopen}
          >
            Reopen
          </StyledButtonWithMargin>
        </Popconfirm>
      )}
      <Popconfirm
        onConfirm={handleTierOneDone}
        disabled={isTierOneDoneCheckboxDisabled}
        title={
          pendingWorkMessage ||
          'This will confirm Tier One Curation Done. Are you sure?'
        }
        placement="bottomRight"
      >
        <Checkbox
          disabled={isTierOneDoneCheckboxDisabled}
          checked={menuCurationTask.isTierOneDone}
        >
          Tier One Done
        </Checkbox>
      </Popconfirm>
      <Popconfirm
        onConfirm={handleUnknownWordsCurationDone}
        disabled={isUnknownWordsCurationDoneCheckboxDisabled}
        title="This will confirm Unknown Words Curation Done. Are you sure?"
        placement="bottomRight"
      >
        <Checkbox
          disabled={isUnknownWordsCurationDoneCheckboxDisabled}
          checked={menuCurationTask.isUnknownWordsCurationDone}
        >
          U.W Curation Done
        </Checkbox>
      </Popconfirm>
      <Popconfirm
        onConfirm={handleUnknownWordsQaDone}
        disabled={isUnknownWordsQaDoneCheckboxDisabled}
        title="This will confirm Unknown Words Qa Done. Are you sure?"
        placement="bottomRight"
      >
        <Checkbox
          disabled={isUnknownWordsQaDoneCheckboxDisabled}
          checked={menuCurationTask.isUnknownWordsQaDone}
        >
          U.W Qa Done
        </Checkbox>
      </Popconfirm>
      <Popconfirm
        onConfirm={handleCurationDone}
        disabled={isCurationDoneCheckboxDisabled}
        title={
          pendingWorkMessage || 'This will confirm Curation Done. Are you sure?'
        }
        placement="bottomRight"
      >
        <Checkbox
          disabled={isCurationDoneCheckboxDisabled}
          checked={menuCurationTask.isCurationDone}
        >
          Curation Done
        </Checkbox>
      </Popconfirm>
      <Popconfirm
        onConfirm={handleQaDone}
        disabled={isQaDoneCheckboxDisabled}
        title={pendingWorkMessage || 'This will confirm QA Done. Are you sure?'}
        placement="bottomRight"
      >
        <Checkbox
          disabled={isQaDoneCheckboxDisabled}
          checked={menuCurationTask.isQaDone}
        >
          Qa Done
        </Checkbox>
      </Popconfirm>
      <Popconfirm
        onConfirm={handleQcDone}
        disabled={isQcDoneCheckboxDisabled}
        title={pendingWorkMessage || 'This will confirm QC Done. Are you sure?'}
        placement="bottomRight"
      >
        <Checkbox
          disabled={isQcDoneCheckboxDisabled}
          checked={menuCurationTask.isQcDone}
        >
          Qc Done
        </Checkbox>
      </Popconfirm>
    </>
  )
}

MenuCurationTaskMenuButtons.propTypes = {
  menuCurationTask: menuCurationTaskPropType.isRequired,
}

export default MenuCurationTaskMenuButtons
